import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface ApiKeyProfileApp {
  appId: string;
  name: string;
}

export interface ApiKeyProfile {
  profileId: string;
  name: string;
  description?: string | null;
  orgId: string;
  apiKey: string;
  secretKeyMasked?: string | null;
  /** Only present on create / regenerate responses */
  secretKey?: string;
  status: 'active' | 'suspended';
  lastUsedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  applications?: ApiKeyProfileApp[];
}

export interface CreateApiKeyProfileRequest {
  name: string;
  description?: string;
  appIds?: string[];
}

export const apiKeyProfilesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getApiKeyProfiles: builder.query<ApiKeyProfile[], void>({
      query: () => '/portal/api-key-profiles',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ profiles: ApiKeyProfile[] }>(response);
        return normalized.profiles ?? [];
      },
      providesTags: ['ApiKeyProfile'],
    }),

    getApiKeyProfile: builder.query<ApiKeyProfile, string>({
      query: (profileId) => `/portal/api-key-profiles/${profileId}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiKeyProfile>(response),
      providesTags: ['ApiKeyProfile'],
    }),

    createApiKeyProfile: builder.mutation<ApiKeyProfile, CreateApiKeyProfileRequest>({
      query: (body) => ({
        url: '/portal/api-key-profiles',
        method: 'POST',
        body: {
          name: body.name,
          description: body.description,
          app_ids: body.appIds ?? [],
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiKeyProfile>(response),
      invalidatesTags: ['ApiKeyProfile', 'Organisation'],
    }),

    updateApiKeyProfile: builder.mutation<
      ApiKeyProfile,
      {
        profileId: string;
        body: Partial<{ name: string; description: string; status: string }>;
      }
    >({
      query: ({ profileId, body }) => ({
        url: `/portal/api-key-profiles/${profileId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiKeyProfile>(response),
      invalidatesTags: ['ApiKeyProfile'],
    }),

    deleteApiKeyProfile: builder.mutation<{ deleted: boolean }, string>({
      query: (profileId) => ({
        url: `/portal/api-key-profiles/${profileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ApiKeyProfile', 'Organisation'],
    }),

    regenerateApiKeyProfile: builder.mutation<ApiKeyProfile, string>({
      query: (profileId) => ({
        url: `/portal/api-key-profiles/${profileId}/regenerate`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiKeyProfile>(response),
      invalidatesTags: ['ApiKeyProfile'],
    }),

    updateApiKeyProfileApps: builder.mutation<
      { profileId: string; appCount: number },
      { profileId: string; appIds: string[] }
    >({
      query: ({ profileId, appIds }) => ({
        url: `/portal/api-key-profiles/${profileId}/apps`,
        method: 'PUT',
        body: { app_ids: appIds },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ profileId: string; appCount: number }>(response),
      invalidatesTags: ['ApiKeyProfile'],
    }),
  }),
});

export const {
  useGetApiKeyProfilesQuery,
  useGetApiKeyProfileQuery,
  useCreateApiKeyProfileMutation,
  useUpdateApiKeyProfileMutation,
  useDeleteApiKeyProfileMutation,
  useRegenerateApiKeyProfileMutation,
  useUpdateApiKeyProfileAppsMutation,
} = apiKeyProfilesApi;
